import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Grid, List, Filter, ChevronDown, Presentation, ArrowUpDown, FileX } from 'lucide-react'
import { PresentationCard, PresentationItem } from './PresentationCard'
import { PresentationCardSkeleton } from './PresentationCardSkeleton'
import { FilterPanel, FilterState } from './FilterPanel'
import { EmptyState } from './EmptyState'
import { PresentationLibraryError } from './PresentationLibraryError'
import { useDebounce } from '@/hooks/useDebounce'
import { isOfflineModeActive } from '@/config/offlineAuth'
import { presentations as localPresentations } from '../data/presentations'
import {
  professionalLayoutsDemo,
  developerTrainingDay1,
  entwicklerschulungTag1,
  contextEngineeringProfessionalEn,
  contextEngineeringProfessionalDe,
  erstgespraech,
  erstgespraechEn
} from '../data/presentations'

export type { PresentationItem } from './PresentationCard'

// Search index interface
interface SearchIndex {
  presentationId: string
  title: string
  description: string
  tags: string[]
  author: string
  allText: string // Combined searchable text
}

// Local storage keys for persistence
const FILTER_STORAGE_KEY = 'presentation-library-filters'
const SORT_STORAGE_KEY = 'presentation-library-sort'

type SortOption = 'title-asc' | 'title-desc' | 'newest' | 'oldest' | 'views'

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'title-asc', label: 'Title A-Z' },
  { value: 'title-desc', label: 'Title Z-A' },
  { value: 'views', label: 'Most Viewed' }
]

interface PresentationLibraryProps {
  onPresentationClick?: (id: string) => void
}

// Map presentation IDs to their full data for thumbnail generation
const presentationDataMap: Record<string, { slides: any[] }> = {
  'professional-layouts-demo': { slides: professionalLayoutsDemo.slides },
  'developer-training-day1': { slides: developerTrainingDay1.slides },
  'entwicklerschulung-tag1': { slides: entwicklerschulungTag1.slides },
  'context-engineering-professional-en': { slides: contextEngineeringProfessionalEn.slides },
  'context-engineering-professional-de': { slides: contextEngineeringProfessionalDe.slides },
  'erstgespraech': { slides: erstgespraech.slides },
  'erstgespraech-en': { slides: erstgespraechEn.slides },
}

// Convert local presentations to PresentationItem format with slide data
const localPresentationsAsItems: PresentationItem[] = localPresentations.map((p) => ({
  id: p.id,
  title: p.title,
  description: p.description,
  type: 'presentation',
  accessLevel: p.accessLevel,
  companyId: null,
  filePath: '',
  thumbnailUrl: null,
  slides: presentationDataMap[p.id]?.slides, // Include slide data for thumbnail generation
  metadata: {
    slideCount: p.slideCount,
    author: p.author,
    tags: p.tags,
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}))

export function PresentationLibrary({ onPresentationClick }: PresentationLibraryProps) {
  const [backendPresentations, setBackendPresentations] = useState<PresentationItem[]>([])
  const [loadingBackend, setLoadingBackend] = useState(true)
  const [backendError, setBackendError] = useState<string | null>(null)
  const [showLocalOnly, setShowLocalOnly] = useState(false)

  // Filter and sort states with localStorage persistence
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    const saved = localStorage.getItem('presentation-library-view-mode')
    return saved === 'list' ? 'list' : 'grid'
  })
  const [searchQuery, setSearchQuery] = useState('')
  // Debounced search query for better performance
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const [sortBy, setSortBy] = useState<SortOption>(() => {
    const saved = localStorage.getItem(SORT_STORAGE_KEY)
    return (saved === 'title-asc' || saved === 'title-desc' || saved === 'newest' || saved === 'oldest' || saved === 'views')
      ? saved as SortOption
      : 'newest'
  })
  const [filterState, setFilterState] = useState<FilterState>(() => {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY)
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        // Invalid JSON, use defaults
      }
    }
    return {
      accessLevel: 'all',
      tags: [],
      authors: []
    }
  })
  const [showFilters, setShowFilters] = useState(false)

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem('presentation-library-view-mode', viewMode)
  }, [viewMode])

  // Persist sort preference to localStorage
  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sortBy)
  }, [sortBy])

  // Persist filter state to localStorage
  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterState))
  }, [filterState])

  // Get user for role-based filtering options
  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null

  // Only show access level filter for company admins and super admins
  const canFilterByAccessLevel = user && (
    user.role === 'company_admin' ||
    user.role === 'super_admin'
  )

  // Fetch backend presentations
  const fetchBackendPresentations = async () => {
    // Check if offline mode is active - skip backend fetch entirely
    if (isOfflineModeActive()) {
      console.log('[PresentationLibrary] Offline mode active - skipping backend fetch, showing local presentations only')
      setBackendPresentations([])
      setLoadingBackend(false)
      setBackendError(null)
      return
    }

    const accessToken = localStorage.getItem('token')
    if (!accessToken) {
      // No token - just show local presentations silently
      setLoadingBackend(false)
      return
    }

    setBackendError(null)
    setShowLocalOnly(false)

    try {
      const response = await fetch('http://localhost:3000/api/presentation-library', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })

      // Handle 401 Unauthorized - silently fall back to local presentations
      if (response.status === 401) {
        console.log('[PresentationLibrary] Not authenticated - showing local presentations only')
        setBackendPresentations([])
        setLoadingBackend(false)
        return
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to fetch presentations')
      }

      const result = await response.json()

      // Handle the response format from /api/presentation-library
      if (result.success && result.data) {
        // Convert presentation-library format to PresentationItem format
        const items: PresentationItem[] = result.data.map((p: any) => ({
          id: p.id,
          title: p.title,
          description: p.description,
          type: 'presentation',
          accessLevel: 'demo', // Default to demo for library presentations
          companyId: null,
          filePath: '',
          thumbnailUrl: p.thumbnailUrl || null,
          slides: presentationDataMap[p.id]?.slides, // Include slide data for thumbnail generation
          metadata: {
            slideCount: p.slideCount,
            author: p.author || 'Unknown',
            tags: p.tags || [],
          },
          createdAt: p.createdAt,
          updatedAt: p.updatedAt,
        }))
        setBackendPresentations(items)
      } else {
        setBackendPresentations([])
      }
    } catch (_err: unknown) {
      // Log error but don't show it to user - they'll still see local presentations
      console.log('[PresentationLibrary] Backend unavailable - showing local presentations only')
      setBackendPresentations([])
    } finally {
      setLoadingBackend(false)
    }
  }

  useEffect(() => {
    fetchBackendPresentations()
  }, [])

  // Merge local and backend presentations (no sorting yet)
  const allPresentations = useMemo(() => {
    // Combine local presentations with backend presentations
    const merged = [...localPresentationsAsItems]

    // Only add backend presentations if we're not showing local only
    if (!showLocalOnly) {
      // Add backend presentations that don't exist locally (by ID)
      const localIds = new Set(localPresentationsAsItems.map(p => p.id))
      for (const bp of backendPresentations) {
        if (!localIds.has(bp.id)) {
          merged.push(bp)
        }
      }
    }

    return merged
  }, [backendPresentations, showLocalOnly])

  // Extract available tags and authors from all presentations
  const { availableTags, availableAuthors } = useMemo(() => {
    const tagsSet = new Set<string>()
    const authorsSet = new Set<string>()

    allPresentations.forEach(p => {
      // Extract tags from metadata
      if (p.metadata?.tags && Array.isArray(p.metadata.tags)) {
        p.metadata.tags.forEach(tag => tagsSet.add(tag))
      }

      // Extract author from metadata
      if (p.metadata?.author) {
        authorsSet.add(p.metadata.author)
      }
    })

    return {
      availableTags: Array.from(tagsSet).sort(),
      availableAuthors: Array.from(authorsSet).sort()
    }
  }, [allPresentations])

  // Build search index for efficient searching
  const searchIndex = useMemo(() => {
    const index: Map<string, SearchIndex> = new Map()

    allPresentations.forEach(p => {
      const tags = p.metadata?.tags || []
      const author = p.metadata?.author || ''

      // Combine all searchable text into one string
      const allText = [
        p.title,
        p.description,
        ...tags,
        author
      ].join(' ').toLowerCase()

      index.set(p.id, {
        presentationId: p.id,
        title: p.title,
        description: p.description,
        tags,
        author,
        allText
      })
    })

    return index
  }, [allPresentations])

  // Filter presentations based on search and filters
  const filteredPresentations = useMemo(() => {
    let filtered = allPresentations

    // Filter by access level
    if (filterState.accessLevel !== 'all') {
      filtered = filtered.filter(p => p.accessLevel === filterState.accessLevel)
    }

    // Filter by tags
    if (filterState.tags.length > 0) {
      filtered = filtered.filter(p => {
        const presentationTags = p.metadata?.tags || []
        return filterState.tags.some(tag => presentationTags.includes(tag))
      })
    }

    // Filter by authors
    if (filterState.authors.length > 0) {
      filtered = filtered.filter(p => {
        const author = p.metadata?.author
        return author !== undefined && filterState.authors.includes(author)
      })
    }

    // Filter by search query (uses debounced value and search index)
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase()
      filtered = filtered.filter(p => {
        const indexEntry = searchIndex.get(p.id)
        if (!indexEntry) return false

        // Search in combined text for better performance
        return indexEntry.allText.includes(query)
      })
    }

    return filtered
  }, [allPresentations, filterState, debouncedSearchQuery, searchIndex])

  // Sort filtered presentations
  const sortedPresentations = useMemo(() => {
    const sorted = [...filteredPresentations]

    switch (sortBy) {
      case 'title-asc':
        sorted.sort((a, b) => a.title.localeCompare(b.title))
        break
      case 'title-desc':
        sorted.sort((a, b) => b.title.localeCompare(a.title))
        break
      case 'newest':
        sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        break
      case 'oldest':
        sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        break
      case 'views':
        sorted.sort((a, b) => (b.metadata?.viewCount || 0) - (a.metadata?.viewCount || 0))
        break
    }

    return sorted
  }, [filteredPresentations, sortBy])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleFilterChange = (newFilters: FilterState) => {
    setFilterState(newFilters)
  }

  const handleClearAllFilters = () => {
    setFilterState({
      accessLevel: 'all',
      tags: [],
      authors: []
    })
  }

  const handlePresentationClick = (presentation: PresentationItem) => {
    // Navigate using the presentation ID (works for both local and backend)
    onPresentationClick?.(presentation.id)
  }

  const handleRetry = () => {
    // Reset states and retry fetching
    setBackendError(null)
    setShowLocalOnly(false)
    setLoadingBackend(true)
    fetchBackendPresentations()
  }

  const handleShowLocal = () => {
    // Show only local presentations
    setShowLocalOnly(true)
    setBackendError(null)
  }

  return (
    <div className="space-y-6">
      {/* Status info */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Showing {sortedPresentations.length} of {allPresentations.length} presentation{allPresentations.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-3 min-h-[44px] min-w-[44px] rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                viewMode === 'grid'
                  ? 'bg-background shadow-sm'
                  : 'hover:bg-background/50'
              }`}
              aria-label="Grid view"
            >
              <Grid className="w-5 h-5 text-foreground" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-3 min-h-[44px] min-w-[44px] rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                viewMode === 'list'
                  ? 'bg-background shadow-sm'
                  : 'hover:bg-background/50'
              }`}
              aria-label="List view"
            >
              <List className="w-5 h-5 text-foreground" />
            </button>
          </div>

          {/* Sort Dropdown */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="appearance-none pl-9 pr-10 py-3 min-h-[44px] bg-background border rounded-lg hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary text-sm cursor-pointer"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row items-start gap-4">
        {/* Search Bar */}
        <div className="flex-1 relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search presentations..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-3 min-h-[44px] bg-background border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        {/* Filter Button */}
        {(canFilterByAccessLevel || availableTags.length > 0 || availableAuthors.length > 0) && (
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-3 min-h-[44px] bg-background border rounded-lg hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={showFilters}
              aria-controls="filter-panel"
            >
              <Filter className="w-4 h-4 text-foreground" />
              <span className="hidden sm:inline">Filters</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-80 sm:w-96 z-50"
                >
                  <FilterPanel
                    filterState={filterState}
                    onFilterChange={handleFilterChange}
                    availableTags={availableTags}
                    availableAuthors={availableAuthors}
                    canFilterByAccessLevel={canFilterByAccessLevel}
                    totalCount={allPresentations.length}
                    filteredCount={sortedPresentations.length}
                    onClearAll={handleClearAllFilters}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Loading State - Show skeleton cards during initial load */}
      {loadingBackend && sortedPresentations.length === 0 && (
        <div className={`grid gap-4 ${
          viewMode === 'grid'
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1'
        }`}>
          {/* Show 6 skeleton cards */}
          {Array.from({ length: 6 }).map((_, index) => (
            <PresentationCardSkeleton
              key={`skeleton-${index}`}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}

      {/* Error State with retry and local fallback */}
      {backendError && !loadingBackend && !showLocalOnly && (
        <PresentationLibraryError
          error={backendError}
          onRetry={handleRetry}
          onShowLocal={handleShowLocal}
          hasLocalPresentations={localPresentationsAsItems.length > 0}
        />
      )}

      {/* Empty State */}
      {!loadingBackend && sortedPresentations.length === 0 && (
        <EmptyState
          icon={searchQuery || filterState.accessLevel !== 'all' || filterState.tags.length > 0 || filterState.authors.length > 0 ? FileX : Presentation}
          title={searchQuery || filterState.accessLevel !== 'all' || filterState.tags.length > 0 || filterState.authors.length > 0 ? 'No presentations found' : 'No presentations available'}
          description={
            searchQuery || filterState.accessLevel !== 'all' || filterState.tags.length > 0 || filterState.authors.length > 0
              ? 'Try adjusting your filters or search query to find what you\'re looking for'
              : 'Get started by creating your first presentation'
          }
          action={
            searchQuery || filterState.accessLevel !== 'all' || filterState.tags.length > 0 || filterState.authors.length > 0
              ? {
                  label: 'Clear All Filters',
                  onClick: handleClearAllFilters
                }
              : undefined
          }
        />
      )}

      {/* Presentations Grid */}
      {!loadingBackend && sortedPresentations.length > 0 && (
        <div className={`grid gap-4 ${
          viewMode === 'grid'
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1'
        }`}>
          <AnimatePresence mode="popLayout">
            {sortedPresentations.map((presentation, index) => (
              <motion.div
                key={presentation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{
                  delay: loadingBackend ? 0 : index * 0.05, // No stagger delay on first load
                  duration: 0.3
                }}
              >
                <PresentationCard
                  presentation={presentation}
                  viewMode={viewMode}
                  onClick={handlePresentationClick}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
